import { isStaffAppPath } from './staffPaths';

// When the login token runs out (or the server no longer accepts it) the app signs the user
// out by itself: session cleared, page reloaded to the sign-in screen with a short notice.
// Nothing stale stays on screen, and signing in again starts from a clean page.
//
// Only a 401 that (a) the server marks as a dead session and (b) was sent with the CURRENT
// staff token counts. A wrong password, another login's token, a Super Admin or client-portal
// token, or a 401 from some integration never logs anyone out.

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
// SESSION_RESET = a maintenance window ended after this login began: everyone signs in fresh.
const SESSION_CODES = new Set(['TOKEN_EXPIRED', 'TOKEN_INVALID', 'NO_TOKEN', 'SESSION_RESET']);
const LEGACY_MESSAGE = /token is not valid|no token, authorization denied/i; // servers not yet sending `code`

export const SESSION_NOTICE_KEY = 'cx-session-notice';
const EXPIRED_NOTICE = 'Your session has expired. Please sign in again.';
const UPDATED_NOTICE = 'ChanakyaX has been updated. Please sign in again to continue.';

const isSessionDead = (b) => !!b && (SESSION_CODES.has(b.code) || (!b.code && LEGACY_MESSAGE.test(String(b.message || ''))));

function bearerFrom(args) {
  const [input, init] = args;
  const h = (init && init.headers) || (input && typeof input === 'object' && input.headers) || null;
  if (!h) return null;
  let v = null;
  if (typeof h.get === 'function') v = h.get('Authorization');
  else if (Array.isArray(h)) v = (h.find(([k]) => String(k).toLowerCase() === 'authorization') || [])[1];
  else { const k = Object.keys(h).find(key => key.toLowerCase() === 'authorization'); v = k ? h[k] : null; }
  return v ? String(v).replace(/^Bearer\s+/i, '') : null;
}

export function createSessionExpiryFetch(nativeFetch, { base, getToken, onExpired }) {
  return function fetchWithSessionExpiry(...args) {
    return nativeFetch(...args).then((res) => {
      if (res.status === 401) {
        const url = String(args[0] && args[0].url ? args[0].url : args[0]);
        const sent = bearerFrom(args);
        if (url.startsWith(base) && sent && sent === getToken()) {
          res.clone().json().then((b) => { if (isSessionDead(b)) onExpired(b.code); }).catch(() => {});
        }
      }
      return res;
    });
  };
}

let expiring = false;
export function expireSession(code) {
  if (expiring) return;
  expiring = true;
  try { localStorage.removeItem('token'); localStorage.removeItem('user'); } catch (_) { /* ignore */ }
  if (isStaffAppPath(window.location.pathname)) {
    try { sessionStorage.setItem(SESSION_NOTICE_KEY, code === 'SESSION_RESET' ? UPDATED_NOTICE : EXPIRED_NOTICE); } catch (_) { /* ignore */ }
    window.location.replace('/');
  } else {
    expiring = false; // a stale token on a login/Super Admin/client page: just forget it
  }
}

// Maintenance just ended on a page that was open through it: drop every login on this device and
// start clean on the freshly deployed version (the server also refuses all older logins).
export function forceFreshLogin() {
  try {
    localStorage.removeItem('token'); localStorage.removeItem('user');
    localStorage.removeItem('client_portal_token');
  } catch (_) { /* ignore */ }
  const p = window.location.pathname;
  if (isStaffAppPath(p)) {
    try { sessionStorage.setItem(SESSION_NOTICE_KEY, UPDATED_NOTICE); } catch (_) { /* ignore */ }
    window.location.replace('/');
  } else if (p.startsWith('/client-portal')) {
    window.location.replace('/client-portal/login');
  } else {
    window.location.reload(); // login / register / portal-login pages: just load the new version
  }
}

export function installSessionExpiry(nativeFetch) {
  return createSessionExpiryFetch(nativeFetch, {
    base: BASE,
    getToken: () => { try { return localStorage.getItem('token'); } catch (_) { return null; } },
    onExpired: expireSession,
  });
}
